import { mapBookingToForm } from 'features/Bookings/components/EditBooking'
import { GTBookingListItem } from 'features/Bookings/components/EditBooking/types'
import { ITicketModel, IBookingModel, IEditBookingModel, GTBookingItem } from 'features/Bookings/types'
import { BookingTicketFragmentFragment, BookingV2Status } from 'gql/generated/graphql'
import {
  GTBookingV2Ticket,
  GTBookingV2Payment,
  GTBookingV2PaymentStatus,
  GTBookingV2TicketCancellationSource,
  GTBookingVertical,
  GTBookingV2Status,
  GTBookingV2TicketAncillaryType
} from 'gql/types.generated'
import { isEmpty } from 'lodash'
import { findValidTicketsByTrips, isTicketCancelled } from 'resolvers/bookings'
import { canBeApplied, canBeCancelled } from 'resolvers/insurance'

export const canCancelInsurance = (tickets: GTBookingV2Ticket[]): boolean => {
  const ticketWithInsurance = tickets.find((ticket) => {
    if (!isTicketCancelled(ticket)) {
      const ancillaries = ticket.ancillaries || []
      const insurance = ancillaries.find(({ type }) => type === GTBookingV2TicketAncillaryType.Insurance)
      return insurance && canBeCancelled(insurance.status)
    }

    return false
  })

  return !isEmpty(ticketWithInsurance)
}
export const createTicketModel = (ticket: GTBookingV2Ticket, bookingPayments: GTBookingV2Payment[]): ITicketModel => {
  let paidAmount = 0
  bookingPayments.forEach((payment) => {
    if (payment.status === GTBookingV2PaymentStatus.Paid) {
      const paidTicket = payment.tickets.find(({ id }: { id: string }) => ticket.id === id)
      if (paidTicket) {
        paidAmount = paidTicket.subtotal - paidTicket.discount + paidTicket.vat
      }
    }
  })
  return {
    ...ticket,
    paidAmount
  }
}
export const canApplyInsurance = (tickets: GTBookingV2Ticket[]): boolean => {
  const ticketWithoutInsurance = tickets.find((ticket) => {
    if (!isTicketCancelled(ticket)) {
      const ancillaries = ticket.ancillaries || []
      const insurance = ancillaries.find(({ type }) => type === GTBookingV2TicketAncillaryType.Insurance)
      return !insurance || canBeApplied(insurance.status, false)
    }

    return false
  })

  return !isEmpty(ticketWithoutInsurance)
}

export const bookingHasReservation = (bookingStatus: GTBookingV2Status): boolean => {
  return (
    bookingStatus === GTBookingV2Status.Reserved ||
    bookingStatus === GTBookingV2Status.Confirmed ||
    bookingStatus === GTBookingV2Status.PartiallyConfirmed
  )
}
export const bookingHasPaidStatus = (bookingStatus: GTBookingV2Status): boolean => {
  return bookingStatus === GTBookingV2Status.Paid
}
export enum EBookingActions {
  Cancellation = 'Cancellation',
  ResendEmails = 'ResendEmails',
  EditBooking = 'EditBooking',
  MoveBooking = 'MoveBooking',
  ApplyInsurance = 'ApplyInsurance',
  CancelInsurance = 'CancelInsurance',
  Notes = 'Notes'
}
const getBookingActionsByVertical = (vertical: string, isHomePickup = false): EBookingActions[] => {
  const TravelActions = [
    EBookingActions.Cancellation,
    EBookingActions.ResendEmails,
    EBookingActions.EditBooking,
    EBookingActions.MoveBooking,
    EBookingActions.ApplyInsurance,
    EBookingActions.CancelInsurance,
    EBookingActions.Notes
  ]
  const CityActions = [
    EBookingActions.Cancellation,
    EBookingActions.ResendEmails,
    EBookingActions.MoveBooking,
    EBookingActions.Notes
  ]

  const TeamsActions = [EBookingActions.Cancellation, EBookingActions.Notes]

  const TeamsWithPickUpServiceActions = [EBookingActions.Notes]
  switch (vertical) {
    case GTBookingVertical.City:
      return CityActions
    case GTBookingVertical.Team:
      if (isHomePickup) {
        return TeamsWithPickUpServiceActions
      }
      return TeamsActions
    default:
      return TravelActions
  }
}
export const createBookingModel = (booking: GTBookingItem): IBookingModel => {
  const { returnTrips, departureTrips } = booking
  const tickets = booking.tickets.map((ticket) => {
    return createTicketModel(ticket as GTBookingV2Ticket, booking.payments as TSFixMe)
  })
  const returnTickets = findValidTicketsByTrips(tickets, returnTrips as TSFixMe)
  const hasReturnTrips = returnTrips.length > 0
  const hasReturnTickets = returnTickets.length > 0

  const departureTickets = findValidTicketsByTrips(tickets, departureTrips as TSFixMe)
  const hasDepartureTrips = departureTrips.length > 0
  const hasDepartureTickets = departureTickets.length > 0
  const hasReservation = bookingHasReservation(booking.status as TSFixMe)

  const isCancelledByUser = (booking.tickets as BookingTicketFragmentFragment[]).every(
    (ticket) => ticket.cancellationSource === GTBookingV2TicketCancellationSource.UserAccount
  )
  const isPartiallyCancelledByUser =
    (booking.tickets as BookingTicketFragmentFragment[]).some(
      (ticket) => ticket.cancellationSource === GTBookingV2TicketCancellationSource.UserAccount
    ) && !isCancelledByUser
  const isBookingCancelled = booking.status === BookingV2Status.Cancelled

  return {
    ...(booking as TSFixMe),
    tickets,
    returnTickets,
    hasReturnTrips,
    hasReturnTickets,
    hasDepartureTrips,
    hasDepartureTickets,
    hasPaidStatus: bookingHasPaidStatus(booking.status as TSFixMe),
    hasReservation,
    isBookingCancelled,
    isCancelledByUser,
    isPartiallyCancelledByUser,
    canApplyInsurance: canApplyInsurance(tickets),
    canCancelInsurance: canCancelInsurance(tickets),
    getBookingsActions: () => getBookingActionsByVertical(booking.vertical, booking.isHomePickup)
  }
}
export const createBookingEditModel = (booking: GTBookingListItem): IEditBookingModel => {
  return {
    ...booking,
    toFormValues: () => mapBookingToForm(booking as TSFixMe)
  }
}
